import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
  spacing,
  radii,
  shadows,
  ButtonText,
  Popover,
  TertiaryButton,
  Link,
  P
} from '@PipelineDeals/shared-react-components'
import { useBusinessCard } from 'components/BusinessCard/context'

export const POPOVER_MAX_HEIGHT = '600px'
export const POPOVER_MAX_WIDTH = '480px'
export const ATTRIBUTES_SECTION_MAX_HEIGHT_INSIDE_POPOVER = '400px'

export const PopoverWrapper = ({ children }) => {
  const { cardMeta: { element } } = useBusinessCard()

  const hasElement = element !== undefined

  const style = {
    borderRadius: radii.md,
    boxShadow: shadows.normal,
    padding: spacing.xl,
    maxHeight: POPOVER_MAX_HEIGHT,
    maxWidth: POPOVER_MAX_WIDTH,
    overflow: 'hidden'
  }

  if (hasElement) {
    return (
      <Popover
        target={element}
        placement="left"
        flip
        show
        hideArrow
        style={style}
      >
        <div className="pld-business-card-popover-wrapper">
          {children}
        </div>
      </Popover>
    )
  } else {
    return <>{children}</>
  }
}

PopoverWrapper.propTypes = {
  children: PropTypes.node
}

export const SectionsWrapper = styled.div`
  display: ${props => props.wrapped ? 'grid' : 'flex'};
  flex-direction: ${props => props.wrapped ? 'unset' : 'column'};
  grid-template-columns: ${props => props.wrapped ? 'repeat(2, 1fr)' : 'unset'};
  gap: ${spacing.xl};
  max-height: ${props => props.maxHeight ? props.maxHeight : 'none'};
  overflow-x: hidden;
  overflow-y: ${props => props.scrollable ? 'auto' : 'hidden'};
  padding-bottom: 1px; // Prevents scrollbar from appearing when content is overflowing
  position: relative;

  // Gradient effect for the bottom if overflowing, unselected in GlobalSearch
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px; // Adjust the height of the gradient
    z-index: 9999999999;
    pointer-events: none;
    display: ${props => (props.overflowing && !props.scrollable ? 'block' : 'none')};

    ${props => (!props.selected && props.overflowing && !props.scrollable) && `
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    `}
  }

    // Gradient effect for the bottom if overflowing, selected by keyboard in GlobalSearch
  ${props => (props.selected && props.overflowing && !props.scrollable) && `
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%);
    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%);
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
  `}
`

export const ShowMoreContainer = styled.div`
  display: flex;
  justify-content: center;
`

export const ShowMoreButton = styled(TertiaryButton)`
  color: ${({ theme }) => theme.colors.blue[600]};
`

export const Sections = ({ children, wrapped, selected, scrollable, maxHeight }) => {
  const { t } = useTranslation()
  const { cardMeta, setCardMeta, resourceId } = useBusinessCard()

  const sectionRef = React.useRef()
  React.useLayoutEffect(() => {
    if (!sectionRef.current) return

    const isOverFlowing = sectionRef.current.clientHeight < sectionRef.current.scrollHeight - 20
    if (isOverFlowing !== cardMeta.overflowing) setCardMeta({ ...cardMeta, overflowing: isOverFlowing })
  }, [sectionRef?.current?.clientHeight, sectionRef?.current?.scrollHeight, resourceId])

  const handleShowMore = () => {
    sectionRef.current.style.maxHeight = parseInt(sectionRef.current.clientHeight) + 280 + 'px'
    if (cardMeta.overflowing) setCardMeta({ ...cardMeta, overflowing: false })
  }

  const sectionWrapperScrollable = cardMeta.element ? true : scrollable
  const sectionWrapperMaxHeight = cardMeta.element ? ATTRIBUTES_SECTION_MAX_HEIGHT_INSIDE_POPOVER : maxHeight

  return (
    <>
      <SectionsWrapper
        ref={sectionRef}
        wrapped={wrapped}
        overflowing={cardMeta.overflowing}
        selected={selected}
        scrollable={sectionWrapperScrollable}
        maxHeight={sectionWrapperMaxHeight}
      >
        {children}
      </SectionsWrapper>
      {(cardMeta.overflowing && !sectionWrapperScrollable) && (
        <ShowMoreContainer>
          <ShowMoreButton
            content={t('common.buttons.showMore')}
            onClick={handleShowMore}
          />
        </ShowMoreContainer>
      )}
    </>
  )
}

Sections.propTypes = {
  children: PropTypes.node,
  wrapped: PropTypes.bool,
  selected: PropTypes.bool,
  scrollable: PropTypes.bool,
  maxHeight: PropTypes.string
}

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xl};
`

export const CardWrapper = ({ children, type = 'card' }) => {
  const { cardMeta: { hideOnLeave }, callbacks: { onClose, setIsOver } } = useBusinessCard()

  useEffect(() => setIsOver === undefined ? undefined : setIsOver(true), [])

  return (
    <CardContainer
      id={`${type.toLowerCase()}-content`}
      onMouseLeave={() => { hideOnLeave && onClose && onClose(false) }}
      onMouseEnter={() => { hideOnLeave && setIsOver && setIsOver(true) }}
    >
      {children}
    </CardContainer>
  )
}

CardWrapper.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string
}

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  align-self: stretch;
  gap: ${spacing.md};

  .edit-link {
    visibility: hidden;
  }

  &:hover {
    .edit-link {
      cursor: pointer;
      visibility: visible;
    }
  }
`

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${spacing.md};

  max-width: 346px;

  // Handle overflowing text
  & > p, h6 {
    max-width: 90%;
  }

  & > p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export const SocialContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${spacing.xl};
`

export const SocialLink = styled.a`
  color: ${({ theme }) => theme.colors.blue[600]};
  font-size: 20px;

  &[disabled] {
    color: ${({ theme }) => theme.colors.gray[300]};
    cursor: default;
    pointer-events: none;
  }
`

const InterpunctIconContainer = styled.i`
  font-size: 5px;
  margin: 0 3px;
  vertical-align: middle;
  padding-bottom: 2px;
`

export const Interpunct = () => {
  return <InterpunctIconContainer className="fas fa-circle" />
}

export const InterpunctJoin = ({ items }) => {
  const filteredItems = items.filter(i => i && i !== undefined && i !== 0)

  return filteredItems.length > 0 ? (
    filteredItems.reduce((result, item) => (
      <>
        {result}&nbsp;<Interpunct />&nbsp;{item}
      </>
    ))
  ) : (
    <React.Fragment /> // eslint-disable-line
  )
}

InterpunctJoin.propTypes = {
  items: PropTypes.array.isRequired
}

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  gap: ${spacing.xl};
  width: 100%;
`

export const HeaderAvatarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${spacing.lg};
  overflow: hidden;
  width: 100%;
  min-width: 0;
`

export const HeaderTitlesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: ${spacing.xxs};

  overflow: hidden;
  min-width: 0;
`

export const HeaderOptionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  flex-shrink: 0;
`

export const Name = styled(ButtonText)`
  color: ${({ theme }) => theme.colors.gray[800]};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;

  overflow-wrap: anywhere;
  max-width: 100%;
`

export const NameLink = styled(Link)`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;

  overflow-wrap: anywhere;
  max-width: 100%;
`

export const EditLink = styled(TertiaryButton)`
  padding: 0px;
  min-width: 24px;
  width: 24px;
  height: 18px;
`

export const RowIconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 18px;
  flex-shrink: 0;
`

export const SupportingText = styled(P)`
  color: ${({ theme }) => theme.colors.gray[500]};
  line-height: 1.2;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`

export const OptionsIcon = styled.i`
  color: ${({ theme }) => theme.colors.gray[700]};
  font-size: 14px;
`

export const RowIcon = styled.i`
  color: ${({ theme }) => theme.colors.blue[600]};
  font-size: 14px;
`
